import { createRouter, createWebHistory } from 'vue-router';

import {getEntityConfigs} from "@/entityConfigs";
import * as openalexId from "@/openalexId";

import HomePage from '@/views/Home.vue'
import SerpPage from '@/views/Serp.vue';
import EntityPage from '@/views/EntityPage.vue';

import AboutPage from '@/views/About.vue';
import AskPage from '@/views/Ask.vue';
import SemanticPage from '@/views/Semantic.vue';

import PageNotFound from "@/views/PageNotFound.vue";


const entityNames = getEntityConfigs()
    .filter(c => c.hasSerp === true)
    .map(c => c.name)
    .join("|")

const redirect = (path, url) => ({
    path,
    name: `redirect-${path.replace(/[^a-zA-Z0-9]/g, '-')}`,
    component: {
        created() {
            window.location.href = url;
        },
        template: '<div>Redirecting...</div>'
    }
});

const routes = [
    {
        path: '/',
        component: HomePage,
        name: 'Home',
    },
    // data pages
    {
        path: `/:entityType(${entityNames})`,
        name: 'Serp',
        component: SerpPage,
    },
    {
        path: '/locations/:entityId(.*)',
        name: 'LocationPage',
        component: EntityPage,
        beforeEnter: (to, from, next) => {
            to.params.entityType = 'locations';
            next();
        }
    },
    {
        path: `/:entityId([waist]\\d+)`,
        name: 'EntityPageShortcut',
        redirect: to => {
            const entityType = openalexId.getEntityType(to.params.entityId)
            return {
                name: "EntityPage",
                params: {
                    entityType,
                    entityId: to.params.entityId,
                },
            }
        }
    },
    {
        path: `/:entityType(${entityNames})/:entityId`,
        name: 'EntityPage',
        component: EntityPage,
    },

    // static pages
    {path: '/about', name: 'About', component: AboutPage},
    {path: '/ask', name: 'Ask', component: AskPage},
    {path: '/semantic', name: 'Semantic', component: SemanticPage},
    {path: '/team', redirect: {name: "Home"}},
    {path: '/transparency', redirect: {name: "Home"}},
    {path: '/legal', redirect: {name: "Home"}},
    {path: '/policies', redirect: {name: "Home"}},
    {path: '/accessibility', redirect: {name: "Home"}},
    {path: '/users', redirect: {name: "Home"}},
    {path: '/testimonials', redirect: {name: "Home"}},
    {path: '/stats', redirect: {name: "Home"}},
    {path: '/collections/:collection_id(.*)', redirect: {name: "Home"}},
    {path: '/discover', redirect: {name: "Home"}},
    {path: '/find', redirect: {name: "Home"}},
    {path: '/query/:pathMatch(.*)*', redirect: {name: "Home"}},
    {path: '/oql-playground/:pathMatch(.*)*', redirect: {name: "Home"}},

    // Docs
    {path: '/data-dump', redirect: {name: "Home"}},
    redirect('/rest-api', "https://developers.openalex.org/how-to-use-the-api/api-overview"),
    {path: '/schema', redirect: {name: "Home"}},
    {path: '/mag-migration-guide', redirect: {name: "Home"}},

    // Forms
    redirect('/author-change-request', "https://docs.google.com/forms/d/e/1FAIpQLSel6otVekIyVOl46eh59mSkruIz32hAnGbJR6KM925E8wiCSg/viewform?usp=sf_link"),
    redirect('/authorChangeRequest', "https://docs.google.com/forms/d/e/1FAIpQLSel6otVekIyVOl46eh59mSkruIz32hAnGbJR6KM925E8wiCSg/viewform?usp=sf_link"),

    // Help center
    redirect('/webinars', "https://help.openalex.org/hc/en-us/articles/24428492324631-Webinars"),
    redirect('/open-houses', "https://help.openalex.org/hc/en-us/articles/24428530346263-Open-houses"),
    redirect('/user-meeting', "https://help.openalex.org/events/user-meeting"),

    // Support
    redirect('/help', "https://help.openalex.org"),
    redirect('/contact', "https://openalex.zendesk.com/hc/requests/new"),
    redirect('/feedback', "https://openalex.zendesk.com/hc/requests/new"),
    redirect('/support', "https://openalex.zendesk.com/hc/requests/new"),

    // Resources
    redirect('/webinars/api-notebook-01', "https://github.com/ourresearch/openalex-api-tutorials/blob/main/notebooks/getting-started/api-webinar-apr2024/tutorial01.ipynb"),

    {path: '/:pathMatch(.*)*', name: "PageNotFound", component: PageNotFound},
];


const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (to.hash) {
            return {
                el: to.hash,
                top: 75
            };
        } else if (savedPosition) {
            return savedPosition;
        } else if (to.name === "Serp") {
            return false;
        } else {
            return {left: 0, top: 0};
        }
    },
});

const redirectFromOldFilters = function (to, from, next) {
    const redirects = {
        "institutions.id": "authorships.institutions.lineage",
    }
    const isRedirectNeeded = Object.keys(redirects).some(key => {
        return to.name === "Serp" && to.fullPath.includes(key)
    })
    if (isRedirectNeeded) {
        let newFullPath = to.fullPath
        Object.keys(redirects).forEach(k => {
            newFullPath = newFullPath.replaceAll(k, redirects[k])
        })
        next(newFullPath)
        return true
    }
    return false
}

router.beforeEach(async (to, from, next) => {
    // Handle legacy v2 parameter
    if (to.query.v2 !== undefined) {
        const newQuery = { ...to.query };
        delete newQuery.v2;
        newQuery['data-version'] = '2';
        return next({
            ...to,
            query: newQuery,
            replace: true
        });
    }

    if (redirectFromOldFilters(to, from, next)) return;

    next();
});

export default router;
